import { effect, inject, resource, Service } from '@angular/core';
import { MatomoTracker } from 'ngx-matomo-client';
import { User } from '../../../auth/user';
import { UserDataClient } from '../../../auth/user-data-client';
import { MatomoCustomDimension } from './matomo-custom-dimension';

@Service()
export class MatomoClient {
  private readonly userDataClient = inject(UserDataClient);
  private readonly matomo = inject(MatomoTracker);

  private readonly hasRememberedConsent = resource<boolean | null, boolean>({
    loader: async () => {
      const cookieConsent = await this.matomo.getRememberedCookieConsent();
      return cookieConsent !== 0;
    },
    defaultValue: null,
  });
  readonly isConsentGiven = this.hasRememberedConsent.asReadonly();

  private readonly user = this.userDataClient.user;

  constructor() {
    this.matomo.requireCookieConsent();
    this.syncUserIdentity();
  }

  private syncUserIdentity(): void {
    effect(() => {
      const user = this.user.hasValue() && this.user.value();
      if (user) {
        this.identify(user);
      } else {
        this.reset();
      }
    });
  }

  identify(user: User): void {
    if (user.sub) {
      this.matomo.setUserId(user.sub);
    }
    this.matomo.setCustomDimension(MatomoCustomDimension.Clients, user.legacy.client.name);
    this.matomo.setCustomDimension(MatomoCustomDimension.Occupation, user.legacy.occupation);
  }

  reset(): void {
    this.matomo.resetUserId();
    this.matomo.deleteCustomDimension(MatomoCustomDimension.Clients);
    this.matomo.deleteCustomDimension(MatomoCustomDimension.Occupation);
  }

  rememberConsent(): void {
    this.matomo.rememberCookieConsentGiven();
    this.hasRememberedConsent.reload();
  }

  forgetConsent(): void {
    this.matomo.forgetCookieConsentGiven();
    this.matomo.deleteCookies();
    this.hasRememberedConsent.reload();
  }
}
